import { useState, useMemo, useCallback } from "react";
import { shouldBlockUnsafeSearchInput } from "@/helpers/sanitizeHelper";

type SearchFilterItem = Record<string, unknown>;
type SearchChangeEvent = {
  target?: {
    value?: string;
  };
};
type SearchChangeValue = SearchChangeEvent | string;
type SearchKeyProperty = string;

const getSearchChangeValue = (e: SearchChangeValue): string => {
  if (typeof e === "string") {
    return e;
  }

  return e?.target?.value ?? "";
};

const useSearchFilter = <T extends SearchFilterItem>(
  data: T[] = [],
  searchKeyProperties: SearchKeyProperty | SearchKeyProperty[] = []
): [string, (_e: SearchChangeValue) => void, T[]] => {
  const [searchKey, setSearchKey] = useState("");

  const normalizedSearchKeyProperties = useMemo(
    () =>
      Array.isArray(searchKeyProperties)
        ? searchKeyProperties
        : [searchKeyProperties], // If it's a string, convert it to an array
    [searchKeyProperties]
  );
  // Handle search input change
  const handleSearchChange = useCallback((e: SearchChangeValue) => {
    const nextValue = getSearchChangeValue(e);
    if (shouldBlockUnsafeSearchInput(nextValue)) return;

    setSearchKey(nextValue);
  }, []);

  const filteredItems = useMemo(() => {
    if (!searchKey || normalizedSearchKeyProperties.length === 0) return data; // If no search key or no properties, return all items

    return data.filter((item) =>
      normalizedSearchKeyProperties.some((key) =>
        item[key]?.toString().toLowerCase().includes(searchKey.toLowerCase())
      )
    );
  }, [searchKey, data, normalizedSearchKeyProperties]);

  return [searchKey, handleSearchChange, filteredItems];
};

export default useSearchFilter;
